/**
 * List items page hook: product selection, MLB preset, listing preview (preset + overrides), publish.
 * State lives in the global store; this hook owns queries and derived state.
 * @typedef {import('../../models/Product').Product} Product
 */
import React, { useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getProducts } from '../../services/productService';
import { getProductCategories } from '../../services/productCategoryService';
import { getProductAdditionalDetailsMlbAttributes } from '../../services/productAdditionalDetailService';
import { getProductPictures, getPicturePublicUrl } from '../../services/productPicturesService';
import { publishListing } from '../../services/listingsService';
import { getProductSuppliers } from '../../services/productSuppliersService';
import { useAppStore } from '../../store/appStore';
import { useClickOutside, PRODUCTS_QUERY_KEY, CATEGORIES_QUERY_KEY, LIST_STALE_TIME_MS } from '../common';
import { buildListingTitle, getListingDefaultsFromProductSuppliers, getEligibleProductSuppliersForListing } from './listingUtils';

export function useListingPage() {
  const { token } = useAuth();
  const listItemsPage = useAppStore((s) => s.listItemsPage);
  const setListItemsPage = useAppStore((s) => s.setListItemsPage);
  const {
    productSearchQuery,
    showProductDropdown,
    selectedProduct,
    mlbDetails,
    mlbLoading,
    mlbError,
    publishLoading,
    publishError,
    publishSuccess,
    listingOverrides,
    listingSupplierSelection,
    showPickListingSuppliersModal
  } = listItemsPage;
  const productDropdownRef = useRef(null);
  const [editingField, setEditingField] = React.useState(null);
  const [editingValue, setEditingValue] = React.useState('');
  const [listingPictureIndex, setListingPictureIndex] = React.useState(0);

  const { data: productsResponse, isLoading: productsLoading } = useQuery({
    queryKey: PRODUCTS_QUERY_KEY,
    queryFn: () => getProducts(token),
    enabled: !!token,
    staleTime: LIST_STALE_TIME_MS
  });

  const { data: categoriesResponse } = useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: () => getProductCategories(token),
    enabled: !!token,
    staleTime: LIST_STALE_TIME_MS
  });

  const { data: productPicturesResponse } = useQuery({
    queryKey: ['product-pictures', selectedProduct?.id],
    queryFn: () => getProductPictures(token, selectedProduct.id),
    enabled: !!token && !!selectedProduct?.id,
    staleTime: LIST_STALE_TIME_MS
  });

  /** Single fetch for product_suppliers: used for listing preview defaults and for Pick listing suppliers modal (no extra call when modal opens). */
  const { data: productSuppliersResponse } = useQuery({
    queryKey: ['product-suppliers', selectedProduct?.id],
    queryFn: () => getProductSuppliers(token, { product_id: selectedProduct.id }),
    enabled: !!token && !!selectedProduct?.id,
    staleTime: LIST_STALE_TIME_MS
  });

  const products = productsResponse?.products ?? [];
  const categories = categoriesResponse?.product_categories ?? [];
  const filteredProducts = useMemo(() => {
    if (!productSearchQuery.trim()) return products;
    const q = productSearchQuery.trim().toLowerCase();
    return products.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [products, productSearchQuery]);

  const categoryDisplay = useMemo(() => {
    if (!selectedProduct || categories.length === 0) {
      return { categoryName: null, subCategoryName: null, mlbCategoryId: null };
    }
    const row = categories.find(
      (r) => r.id === selectedProduct.category_id && r.sub_category_id === selectedProduct.sub_category_id
    );
    return {
      categoryName: row?.category_name ?? null,
      subCategoryName: row?.sub_category_name ?? null,
      mlbCategoryId: row?.mlb_category_child_id || row?.mlb_category_id || null
    };
  }, [selectedProduct, categories]);

  const listingPictures = useMemo(() => {
    const list = productPicturesResponse?.product_pictures ?? [];
    return list
      .map((p) => ({ source: p.picture_url || getPicturePublicUrl(p.picture_path) }))
      .filter((p) => p.source);
  }, [productPicturesResponse]);

  useClickOutside(productDropdownRef, () => setListItemsPage((prev) => ({ ...prev, showProductDropdown: false })));

  React.useEffect(() => {
    setEditingField(null);
  }, [selectedProduct?.id]);

  React.useEffect(() => {
    setListingPictureIndex(0);
  }, [selectedProduct?.id, listingPictures.length]);

  const handleProductSearchChange = (e) => {
    setListItemsPage({
      productSearchQuery: e.target.value,
      selectedProduct: null,
      showProductDropdown: true,
      mlbDetails: null,
      mlbError: ''
    });
  };

  const handleSelectProduct = (product) => {
    setListItemsPage({
      selectedProduct: product,
      productSearchQuery: product.name || '',
      showProductDropdown: false,
      mlbDetails: null,
      mlbError: '',
      publishError: '',
      publishSuccess: '',
      listingOverrides: {},
      showPickListingSuppliersModal: false
    });
  };

  const handleOpenPickSuppliersModal = () => {
    setListItemsPage((prev) => ({ ...prev, showPickListingSuppliersModal: true }));
  };

  const handleSavePickSuppliersModal = (entries) => {
    if (!selectedProduct?.id) return;
    const byId = {};
    let totalQty = 0;
    entries.forEach((e) => {
      byId[e.products_suppliers_id] = {
        selected_quantity: e.selected_quantity,
        priority: Number.isFinite(e.priority) ? e.priority : 0
      };
      totalQty += e.selected_quantity;
    });
    setListItemsPage((prev) => ({
      ...prev,
      listingSupplierSelection: {
        ...prev.listingSupplierSelection,
        [selectedProduct.id]: byId
      },
      listingOverrides: {
        ...prev.listingOverrides,
        available_quantity: String(totalQty)
      },
      showPickListingSuppliersModal: false
    }));
  };

  const handleClosePickSuppliersModal = () => {
    setListItemsPage((prev) => ({ ...prev, showPickListingSuppliersModal: false }));
  };

  const handleLoadMlbPreset = async () => {
    if (!token || !selectedProduct?.id) return;
    setListItemsPage({ mlbError: '', publishError: '', publishSuccess: '', mlbLoading: true });
    try {
      const result = await getProductAdditionalDetailsMlbAttributes(token, selectedProduct.id);
      if (result.success && result.product_additional_details) {
        setListItemsPage({ mlbDetails: result.product_additional_details, mlbLoading: false });
      } else {
        setListItemsPage({ mlbDetails: [], mlbLoading: false });
      }
    } catch (err) {
      setListItemsPage({
        mlbError: err.message || 'Failed to load MLB preset',
        mlbDetails: null,
        mlbLoading: false
      });
    }
  };

  const listingTitle = useMemo(() => buildListingTitle(selectedProduct), [selectedProduct]);

  const productSuppliers = productSuppliersResponse?.product_suppliers ?? [];

  /** Same data for preview and modal: filter once in memory (active, shared_stock, Home/Meli). */
  const eligibleProductSuppliers = useMemo(
    () => getEligibleProductSuppliersForListing(productSuppliers),
    [productSuppliers]
  );

  const listingPreview = useMemo(() => {
    if (!selectedProduct) return null;
    const selectionForProduct = selectedProduct.id != null ? listingSupplierSelection[selectedProduct.id] : null;
    let defaultAvailableQuantity;
    if (selectionForProduct && typeof selectionForProduct === 'object') {
      defaultAvailableQuantity = Object.values(selectionForProduct).reduce((sum, val) => {
        const q = typeof val === 'object' && val != null && Number.isFinite(val.selected_quantity) ? val.selected_quantity : (Number.isFinite(val) ? val : 0);
        return sum + (q >= 0 ? q : 0);
      }, 0);
    } else {
      defaultAvailableQuantity = getListingDefaultsFromProductSuppliers(eligibleProductSuppliers).defaultAvailableQuantity;
    }
    const { defaultPrice } = getListingDefaultsFromProductSuppliers(eligibleProductSuppliers);
    const saleTerms =
      selectedProduct.warranty != null && selectedProduct.warranty !== ''
        ? [{ id: 'WARRANTY_TYPE', value_name: selectedProduct.warranty }]
        : [];
    const attributes =
      Array.isArray(mlbDetails) && mlbDetails.length > 0
        ? mlbDetails
            .filter((d) => d.mlb_attribute_id != null && d.mlb_attribute_id !== '')
            .map((d) => ({ id: String(d.mlb_attribute_id), value_name: d.detail_value ?? '—' }))
        : [];
    return {
      title: listingTitle,
      category_id: categoryDisplay.mlbCategoryId ?? '—',
      price: defaultPrice != null ? String(defaultPrice) : '—',
      currency_id: 'BRL',
      available_quantity: String(defaultAvailableQuantity),
      buying_mode: 'buy_it_now',
      condition: selectedProduct.condition ?? '—',
      listing_type_id: 'free',
      sale_terms: saleTerms,
      pictures: listingPictures,
      attributes
    };
  }, [selectedProduct, categoryDisplay.mlbCategoryId, mlbDetails, listingTitle, listingPictures, eligibleProductSuppliers, listingSupplierSelection]);


  const effectiveListing = useMemo(() => {
    if (!listingPreview) return null;
    const saleTerms =
      listingOverrides.sale_terms_value != null && String(listingOverrides.sale_terms_value).trim() !== ''
        ? [{ id: 'WARRANTY_TYPE', value_name: String(listingOverrides.sale_terms_value).trim() }]
        : listingPreview.sale_terms;
    const attributes = listingPreview.attributes.map((a) => ({
      id: a.id,
      value_name: listingOverrides.attributeValues?.[a.id] ?? a.value_name
    }));
    return {
      ...listingPreview,
      title: listingOverrides.title ?? listingPreview.title,
      category_id: listingOverrides.category_id ?? listingPreview.category_id,
      condition: listingOverrides.condition ?? listingPreview.condition,
      price: listingOverrides.price ?? listingPreview.price,
      currency_id: listingOverrides.currency_id ?? listingPreview.currency_id,
      available_quantity: listingOverrides.available_quantity ?? listingPreview.available_quantity,
      buying_mode: listingOverrides.buying_mode ?? listingPreview.buying_mode,
      listing_type_id: listingOverrides.listing_type_id ?? listingPreview.listing_type_id,
      sale_terms: saleTerms,
      attributes
    };
  }, [listingPreview, listingOverrides]);

  const handlePublishToMlb = async () => {
    if (!token || !selectedProduct?.id || !effectiveListing) return;
    const categoryId = categoryDisplay.mlbCategoryId;
    const categoryIdToSend = (effectiveListing.category_id && effectiveListing.category_id !== '—')
      ? effectiveListing.category_id
      : categoryId;
    if (!categoryIdToSend || categoryIdToSend === '—') {
      setListItemsPage({ publishError: 'Product category must have an MLB category to publish.' });
      return;
    }
    if (!eligibleProductSuppliers.length) {
      setListItemsPage({ publishError: 'Product must have at least one eligible product-supplier (active, shared stock, Home/Meli) to publish.' });
      return;
    }
    const condition = (effectiveListing.condition === 'new' || effectiveListing.condition === 'used')
      ? effectiveListing.condition
      : 'new';
    const priceNum = parseFloat(effectiveListing.price, 10);
    const quantityNum = parseInt(effectiveListing.available_quantity, 10);
    setListItemsPage({ publishError: '', publishSuccess: '', publishLoading: true });
    try {
      const mlb_item = {
        title: effectiveListing.title,
        category_id: categoryIdToSend,
        price: Number.isFinite(priceNum) && priceNum > 0 ? priceNum : 1,
        currency_id: effectiveListing.currency_id && effectiveListing.currency_id !== '—' ? effectiveListing.currency_id : 'BRL',
        available_quantity: Number.isInteger(quantityNum) && quantityNum >= 0 ? quantityNum : 1,
        buying_mode: effectiveListing.buying_mode && effectiveListing.buying_mode !== '—' ? effectiveListing.buying_mode : 'buy_it_now',
        condition,
        listing_type_id: effectiveListing.listing_type_id && effectiveListing.listing_type_id !== '—' ? effectiveListing.listing_type_id : 'free',
        sale_terms: effectiveListing.sale_terms,
        pictures: effectiveListing.pictures,
        attributes: effectiveListing.attributes,
        description: selectedProduct.description ? String(selectedProduct.description).trim() || undefined : undefined
      };
      const selectionForProduct = listingSupplierSelection[selectedProduct.id];
      let listing_supplier_entries;
      if (selectionForProduct && typeof selectionForProduct === 'object' && Object.keys(selectionForProduct).length > 0) {
        listing_supplier_entries = Object.entries(selectionForProduct)
          .filter(([, val]) => {
            const q = typeof val === 'object' && val != null && Number.isFinite(val.selected_quantity) ? val.selected_quantity : (Number.isFinite(val) ? val : 0);
            return q > 0;
          })
          .map(([psId, val]) => {
            const q = typeof val === 'object' && val != null && Number.isFinite(val.selected_quantity) ? val.selected_quantity : (Number.isFinite(val) ? val : 0);
            const priority = typeof val === 'object' && val != null && Number.isFinite(val.priority) ? val.priority : 0;
            return { products_suppliers_id: Number(psId), selected_quantity: Number(q), priority: Number(priority) };
          });
      } else {
        listing_supplier_entries = eligibleProductSuppliers
          .filter((ps) => ps.id != null)
          .map((ps) => ({
            products_suppliers_id: ps.id,
            selected_quantity: ps.available_amount != null && Number(ps.available_amount) >= 0 ? Math.floor(Number(ps.available_amount)) : 0,
            priority: 0
          }))
          .filter((e) => e.selected_quantity > 0);
      }
      if (listing_supplier_entries.length === 0) {
        setListItemsPage({ publishError: 'Select at least one supplier with quantity > 0 (use Pick listing suppliers).', publishLoading: false });
        return;
      }
      const result = await publishListing(token, { product_id: selectedProduct.id, mlb_item, listing_supplier_entries });
      if (result.success) {
        setListItemsPage({
          publishSuccess: result.listing?.listing_url ? 'Listing published and saved.' : 'Listing published.',
          publishLoading: false
        });
      } else {
        setListItemsPage({ publishError: result.error || 'Failed to publish listing', publishLoading: false });
      }
    } catch (err) {
      setListItemsPage({ publishError: err.message || 'Failed to publish listing', publishLoading: false });
    }
  };

  const basicInfoRows = selectedProduct
    ? [
        { label: 'Name', value: selectedProduct.name },
        { label: 'Brand', value: selectedProduct.brand },
        { label: 'Model', value: selectedProduct.product_model },
        { label: 'SKU', value: selectedProduct.sku },
        { label: 'Description', value: selectedProduct.description }
      ]
    : [];
  const classificationRows = selectedProduct
    ? [
        { label: 'Condition', value: selectedProduct.condition },
        { label: 'Warranty', value: selectedProduct.warranty },
        { label: 'Category', value: categoryDisplay.categoryName },
        { label: 'Sub-category', value: categoryDisplay.subCategoryName }
      ]
    : [];

  const handleStartEdit = (fieldKey, displayValue) => {
    setEditingField(fieldKey);
    setEditingValue(displayValue ?? '');
  };

  const handleSaveEdit = () => {
    if (editingField == null) return;
    if (editingField.startsWith('attr_')) {
      const attrId = editingField.replace(/^attr_/, '');
      setListItemsPage((prev) => ({
        ...prev,
        listingOverrides: {
          ...prev.listingOverrides,
          attributeValues: {
            ...(prev.listingOverrides.attributeValues || {}),
            [attrId]: editingValue
          }
        }
      }));
    } else if (editingField === 'sale_terms') {
      setListItemsPage((prev) => ({
        ...prev,
        listingOverrides: { ...prev.listingOverrides, sale_terms_value: editingValue }
      }));
    } else {
      setListItemsPage((prev) => ({
        ...prev,
        listingOverrides: { ...prev.listingOverrides, [editingField]: editingValue }
      }));
    }
    setEditingField(null);
  };

  const initialSelectionForModal = selectedProduct?.id != null ? listingSupplierSelection[selectedProduct.id] : null;

  return {
    productDropdownRef,
    productSearchQuery,
    showProductDropdown,
    setListItemsPage,
    selectedProduct,
    productsLoading,
    filteredProducts,
    handleProductSearchChange,
    handleSelectProduct,
    mlbDetails,
    mlbError,
    mlbLoading,
    publishLoading,
    publishError,
    publishSuccess,
    handleLoadMlbPreset,
    handlePublishToMlb,
    basicInfoRows,
    classificationRows,
    listingPictures,
    listingPictureIndex,
    setListingPictureIndex,
    effectiveListing,
    editingField,
    editingValue,
    setEditingValue,
    handleStartEdit,
    handleSaveEdit,
    eligibleProductSuppliers,
    showPickListingSuppliersModal,
    handleOpenPickSuppliersModal,
    handleSavePickSuppliersModal,
    handleClosePickSuppliersModal,
    initialSelectionForModal
  };
}
